import { t } from '@lingui/core/macro';
import { lendingApiFetchJson } from 'lendingApi/lendingApi';
import { URLS } from 'utils/tests/urls.config';
import * as yup from 'yup';
import {
  handlePatchOnboardingFilesUploaded,
  handleSaveDocuments,
} from '../../IntroducerOnboarding/RegistrationForm/registrationForm.helpers';
import { TTitle } from '../../registration.types';
import { fullRegistrationSchemaDE } from '../../registrationSchema';
import { TSubmitBrokerOnboardingParams } from './onboardingFormDE.types';

// Onboarding flow adds the DE documents on top of the base DE registration
// schema. Built lazily so the `t` macro resolves against the active locale.
export const getOnboardingSchemaDE = () =>
  fullRegistrationSchemaDE.shape({
    introducerType: yup.string().oneOf(['broker', 'partner']).required(),
    brokerQuestionnaireFile: yup
      .array()
      .of(yup.mixed())
      .min(1, t`Partner questionnaire is required`)
      .required(t`Partner questionnaire is required`),
    hrTradeCertificateFile: yup
      .array()
      .of(yup.mixed())
      .min(1, t`Commercial register extract or trade licence is required`)
      .required(t`Commercial register extract or trade licence is required`),
    // The §34c permit is only required for brokers, not partners.
    license34cFile: yup
      .array()
      .of(yup.mixed())
      .when('introducerType', {
        is: 'broker',
        then: (schema) =>
          schema
            .min(1, t`§34c GewO permit is required`)
            .required(t`§34c GewO permit is required`),
        otherwise: (schema) => schema.notRequired(),
      }),
    certifiedIdFile: yup
      .array()
      .of(yup.mixed())
      .min(1, t`ID card is required`)
      .required(t`ID card is required`),
  });

// Dedicated to the broker/partner onboarding flow so the legacy
// submitDERegistration (accountant/invite paths) stays untouched. Creates the
// account, uploads the documents, then flags onboarding_files_uploaded.
export const submitOnboardingDE = async ({
  values,
  onSuccess,
  onError,
}: TSubmitBrokerOnboardingParams) => {
  const introducerValues = {
    // Fixes type error as values.title can be 'herr' | 'frau' whereas lapi client types expect UK values only
    title: values.title as TTitle,
    first_name: values.firstName,
    last_name: values.lastName,
    email: values.email,
    phone_number: values.phoneNumber,
  };

  try {
    const response = await lendingApiFetchJson(URLS.postIntroducerAccount(), {
      method: 'post',
      body: JSON.stringify({
        data: {
          introducer_account: {
            introducer_type: values.introducerType,
            address: values.registeredTradingAddress,
            company_name: values.companyName,
            company_number: values.companyNumber,
            email: values.email,
            phone_number: values.phoneNumber,
            post_code: values.postcode,
            vat_number: values.vatNumber,
            website: values.companyWebsite,
            onboarding_files_uploaded: false,
          },
          introducer: {
            ...introducerValues,
          },
        },
      }),
    });

    const introducerId = response?.data?.introducer_id;
    const introducerAccountId = response?.data?.introducer_account_id;

    const requiredFields = [
      values.brokerQuestionnaireFile,
      values.hrTradeCertificateFile,
      values.certifiedIdFile,
      // Only brokers upload the §34c permit.
      ...(values.introducerType === 'broker' ? [values.license34cFile] : []),
    ];

    const documents = requiredFields.flatMap((field) => field ?? []);

    const allRequiredDocsPresent = requiredFields.every(
      (field) => (field?.length ?? 0) > 0,
    );

    if (introducerId && introducerAccountId && allRequiredDocsPresent) {
      await handleSaveDocuments({
        selectedFiles: documents,
        introducerId,
      });

      await handlePatchOnboardingFilesUploaded({ introducerAccountId });

      await onSuccess();
      return;
    }

    // The account POST resolved but the response was missing the ids needed to
    // upload documents — treat as a failure and keep the user on the form
    // rather than navigating away with no docs uploaded.
    onError();
    return;
  } catch {
    // Case A: any failure (incl. account creation) keeps the user on the form
    // so they can re-submit.
    onError();
  }
};
